import { computed, ref } from "vue";
import {
  IUser,
  ILoggedInUser,
  IHomePageTiles,
  IUserDetails,
  ISubscriberInfo,
} from "@/common/api/unifiedPortal/interfaces";
import { identifyPendo } from "@/common/pendo";
import { UserType } from "@/globalEnums";
import { defineStore } from "pinia";
import { emailVerificationPageState } from "@/globalEnums";
export * from "./userInterfaces";

export const useUserStore = defineStore("user", () => {
  const homeTilesInitialState = {
    backupOverview: true,
    securityOverview: true,
    securityZeroState: true,
    backupZeroState: true,
    subscriptions: true,
  };

  const email = ref("");
  const currentUser = ref<IUser | null>(null);
  const userType = ref<UserType>(UserType.consumer);
  const selectedLanguage = ref("");
  const isCreateAccountKeycodeExpired = ref(false);
  const createAccountEmailMismatchKeycode = ref("");
  const isResellerManaged = ref(false);
  const checkUserSecurityQuestionsFilled = ref(false);
  const twoFactorAuthAddedFromFirstLogin = ref(false);
  const userDetails = ref<IUserDetails | null>(null);
  const lastRequestTime = ref(0);
  const displayedManageResellerControlRequest = ref(false);
  const showTile = ref<IHomePageTiles>(homeTilesInitialState);
  const isPendoIdentified = ref(false);
  const isCssbUser = ref(false);
  const preliminaryKeepAliveTimeInMinutes = ref(0);
  const preliminaryUserTimedOut = ref(false);
  const returnRoute = ref("");
  const emailVerificationPageState = ref<emailVerificationPageState | null>(null);
  const isSmbAccount = computed(() => {
    const id = currentUser.value?.subscriberInfo?.accountUID;
    if (!id) {
      return false;
    }

    return id > 0;
  });

  const isLoggedInBySso = computed(() => {
    // this doesn't work in localhost, only in dev, stage, etc because we cannot read the cookie in the localhost
    return currentUser.value?.subscriberInfo?.isLoggedInUsingThirdParty ?? false;
  });

  const hasValidatedEmail = computed(() => {
    return (currentUser.value?.subscriberInfo?.hasValidatedEmail || isLoggedInBySso.value) ?? false;
  });

  //Need to create our own reset function because this style of store doesn't support $reset
  function resetState() {
    email.value = "";
    currentUser.value = null;
    userType.value = UserType.consumer;
    //selectedLanguage.value = ""; //This causes the Zuora IFrame to reload...
    isCreateAccountKeycodeExpired.value = false;
    createAccountEmailMismatchKeycode.value = "";
    isResellerManaged.value = false;
    checkUserSecurityQuestionsFilled.value = false;
    twoFactorAuthAddedFromFirstLogin.value = false;
    userDetails.value = null;
    lastRequestTime.value = 0;
    displayedManageResellerControlRequest.value = false;
    showTile.value = homeTilesInitialState;
    isPendoIdentified.value = false;
    isCssbUser.value = false;
    emailVerificationPageState.value = null;
  }

  const userFullName = computed(() => {
    let name = "";
    if (currentUser.value) {
      if (currentUser.value.firstName) {
        name = `${currentUser.value.firstName}`;
      }

      if (currentUser.value.lastName) {
        name = `${name} ${currentUser.value.lastName}`;
      }
    }

    return name;
  });

  const isEstablishedAccount = computed(() => {
    return (currentUser.value && currentUser.value.subscriberInfo?.hasPasswordSet) || false;
  });

  function setUser(user: IUser) {
    currentUser.value = user;

    // Initialize Pendo
    if (user?.personId > 0) {
      identifyPendo(user.personId);
    }
  }

  function setLoggedInUser(user: ILoggedInUser | null) {
    // fix: do not override subscriberInfo information:
    const subscriberInfo = currentUser.value?.subscriberInfo
      ? currentUser.value.subscriberInfo
      : ({} as ISubscriberInfo);
    subscriberInfo.hasValidatedEmail = user?.hasValidatedEmail ?? false;

    const updateCurrentUser = {
      personId: user?.personId,
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      alternateEmail: user?.alternateEmail,
      subscriberInfo: subscriberInfo,
    } as IUser;

    if (currentUser.value) {
      currentUser.value = Object.assign(currentUser.value, updateCurrentUser);
    } else {
      currentUser.value = updateCurrentUser;
    }
  }

  function setEmailVerificationPageState(state: emailVerificationPageState | null) {
    emailVerificationPageState.value = state;
  }

  function resetEmailVerificationPageState() {
    emailVerificationPageState.value = null;
  }

  return {
    email,
    currentUser,
    userType,
    selectedLanguage,
    isCreateAccountKeycodeExpired,
    createAccountEmailMismatchKeycode,
    isResellerManaged,
    checkUserSecurityQuestionsFilled,
    twoFactorAuthAddedFromFirstLogin,
    userDetails,
    lastRequestTime,
    displayedManageResellerControlRequest,
    showTile,
    isPendoIdentified,
    isCssbUser,
    userFullName,
    setUser,
    setLoggedInUser,
    isEstablishedAccount,
    returnRoute,
    resetState,
    preliminaryKeepAliveTimeInMinutes,
    preliminaryUserTimedOut,
    isSmbAccount,
    isLoggedInBySso,
    hasValidatedEmail,
    emailVerificationPageState,
    resetEmailVerificationPageState,
    setEmailVerificationPageState,
  };
});
